/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef, useState} from 'react';
import {ColDef, GridApi} from 'ag-grid-community';
import {GenericObject} from '@ion/core/src/types/entities';
import useInitialState from '../hooks/useInitialState';
import {useIsPhone} from '../hooks/useMedia';
import {
  getCheckboxColumn,
  getColumnDefinitions
} from './ColumnDefinitions';
import {
  definitionsToState
} from './ColumnResolver';
import {GridOperation} from './IonGrid';
import {
  hasChangeInColumnOrder,
  hasChangeInDefinitions,
  SelectedRows,
  selectedRowStyles
} from './IonGridUtils';

export type ColumnDefinitions = {
  desktop: ColDef[],
  mobile?: ColDef[]
}

export interface UseColumnDefinitionsType extends SelectedRows, GridOperation {
  gridApi: GridApi;
  columnDefinitions: ColDef[] | ColumnDefinitions;
  checkboxesEnabled?: boolean;
  rowMenusEnabled?: boolean;
  secondaryRowMenusEnabled?: boolean;
  ctaProps?: GenericObject;
  secondaryCtaProps?: GenericObject;
  fetching?: boolean;
  totalRowCount?: number;
  isClientModel?: boolean;
  pageSize?: number;
  enableSelectAll?: boolean;
  orderEnabled?: boolean;
};

/**
 * @useColumnDefinitions
 *
 * Provides a Hook for setting the column definitions when the grid is ready to receive
 * them. Uses media hook to determine which definition should be used.
 *
 * @param  {string} actionsColumnId optional param to set the field value for the
 *         cta column.
 * @param  {Array<any> | object} columnDefinitions column definitions that will default to
 *         desktop if only an array is provided, otherwise pass an object that will contain.
 *         a desktop property and mobile property and provide the definitions array to each
 * @param  {boolean} gridReady boolean value from useState hook that would trigger this
 *         useEffect hook to run when updated.
 * @param  {boolean} checkboxesEnabled determines whether the checkboxes will be rendered.
 * @param  {boolean} rowMenusEnabled determines whether the row menus will be rendered.
 * @param  {boolean} secondaryRowMenusEnabled determines whether an additional row menu column will be rendered.
 *
 * ```typescript
 *
 *   const columnDefs = useColumnDefinitions({
 *     actionsColumnId : 'someValue',
 *     columnDefinitionsMobile,
 *     columnDefinitionsDesktop,
 *     gridReady: true,
 *     checkboxesEnabled: false,
 *     rowMenuEnabled: false
 *   });
 *
 * ```
 */
export const useColumnDefinitions = ({
  columnDefinitions,
  checkboxesEnabled,
  rowMenusEnabled,
  secondaryRowMenusEnabled,
  gridApi,
  totalRowCount,
  pageSize,
  setAreTotalRowsSelected,
  enableSelectAll = false,
  ctaProps = {},
  secondaryCtaProps = {},
  fetching = false,
  orderEnabled = false,
  areTotalRowsSelected = false,
  isPersist = false,
  isClientModel = false
}: UseColumnDefinitionsType): ColDef[] => {
  const isPhone                  = useIsPhone();
  const columnOrderRef           = useRef<string[]>([]);
  const checkBoxColumnDefinition = getCheckboxColumn({
    totalRowCount,
    pageSize,
    enableSelectAll,
    setAreTotalRowsSelected,
    isClientModel
  });

  const [columnDefs, setColumnDefs] = useState(useInitialState({
    generator : getColumnDefinitions,
    params    : {
      checkBoxColumnDefinition,
      columnDefinitions,
      checkboxesEnabled,
      rowMenusEnabled,
      secondaryRowMenusEnabled,
      isPhone,
      ctaProps,
      secondaryCtaProps
    }
  }));

  useEffect(() => {
    if (gridApi) {
      if (!isPersist) {
        // Total rows selected in the grid disabled checkbox selection
        const checkStyle = areTotalRowsSelected ? selectedRowStyles(true) : selectedRowStyles();
        checkBoxColumnDefinition.cellStyle = checkStyle;
      }

      const definitions = getColumnDefinitions({
        checkBoxColumnDefinition,
        columnDefinitions,
        checkboxesEnabled,
        rowMenusEnabled,
        secondaryRowMenusEnabled,
        isPhone,
        ctaProps,
        secondaryCtaProps
      });
      // apply column definitions to ag-grid only if we have absolute changes...
      if (definitions) {
        const hasChanged       = hasChangeInDefinitions(definitions, gridApi.getColumnDefs());
        const hasChangeInOrder = hasChangeInColumnOrder(
          columnOrderRef,
          gridApi.getAllDisplayedColumns(),
          orderEnabled
        );
        if (hasChanged || hasChangeInOrder) {
          gridApi.setGridOption('columnDefs', definitions);
          gridApi.applyColumnState({
            state      : definitionsToState(definitions),
            applyOrder : true
          });
          setColumnDefs(definitions);
        }
      }
    };
  }, [gridApi, columnDefinitions, totalRowCount, pageSize]);
  return columnDefs;
};

export default useColumnDefinitions;
